import {
  type ActionLogEntry,
  type ArchivedCompany,
  type Cell,
  type ClaudeCredential,
  type Company,
  type CompanyWithSetup,
  type ContextFile,
  type CreateCellVersionInput,
  type CreateClaudeCredentialInput,
  type CreateCompanyInput,
  type CreateDimensionGenerationJobInput,
  type CreateDimensionGenerationJobResult,
  type CreateFolderInput,
  type CreateFolderNoteInput,
  type CreateMatrixGenerationJobInput,
  type CreateMatrixInput,
  type CreateMatrixNoteInput,
  type DeriveMatrixResponse,
  type DigestVersion,
  type Dimension,
  type DimensionGenerationJob,
  type DocsData,
  type DuplicateMatrixResponse,
  EXPORT_RENDER_FACTS_HEADER,
  type EditDigestInput,
  type ExportRenderFacts,
  type ExportRequest,
  type FeatureFlag,
  type FolderNotesResponse,
  type ImportedWorkbook,
  type LogoFraming,
  type Matrix,
  type MatrixDigestState,
  type MatrixFolder,
  type MatrixGenerationJob,
  type MatrixMemo,
  type MatrixNotesResponse,
  type MatrixPromptHistory,
  type MatrixSummary,
  type MatrixWithDimensions,
  type MoveMatrixToFolderResponse,
  type MyCompanyMembership,
  type Note,
  type NoteThread,
  type PalierNumber,
  type PatchCompanyInput,
  type PatchMatrixContextInput,
  type PatchMatrixInput,
  type PatchMatrixViewStateInput,
  type PerformResizeInput,
  type PerformVisibilityInput,
  type RegenerateActivationBlockInput,
  type RegenerateCellInput,
  type RegenerateCellsInput,
  type RegenerateColumnInput,
  type RegenerateDigestInput,
  type RegenerateMatrixInput,
  type RegenerateRowInput,
  type RegenerationBatch,
  type RegenerationPromptPreview,
  type RegenerationPromptPreviewInput,
  type ReplaceDimensionsInput,
  type ReplyNoteInput,
  type RestoreRegenerationBatchResult,
  type StartDigestJobInput,
  type SynthesisVersion,
  type TahitiEnvironment,
  type TahitiEnvironmentValue,
  type UpdateFolderInput,
  type UpdateMemoInput,
  type UpdateNoteInput,
  type UpdatePaliersInput,
  type UpdateSingularitySetupInput,
  type User,
  actionLogSchema,
  archivedCompanySchema,
  cellSchema,
  claudeCredentialSchema,
  companySchema,
  companyWithSetupSchema,
  composedPromptSchema,
  contextFileSchema,
  createDimensionGenerationJobResultSchema,
  deriveMatrixResponseSchema,
  digestVersionSchema,
  dimensionGenerationJobSchema,
  dimensionSchema,
  docsDataSchema,
  duplicateMatrixResponseSchema,
  exportRenderFactsSchema,
  featureFlagSchema,
  folderNotesResponseSchema,
  importedWorkbookSchema,
  matrixDigestStateSchema,
  matrixFolderSchema,
  matrixGenerationJobSchema,
  matrixMemoSchema,
  matrixNotesResponseSchema,
  matrixPromptHistorySchema,
  matrixSchema,
  matrixSummarySchema,
  matrixWithDimensionsSchema,
  moveMatrixToFolderResponseSchema,
  myCompanyMembershipSchema,
  noteSchema,
  noteThreadSchema,
  regenerationBatchSchema,
  regenerationPromptPreviewSchema,
  restoreRegenerationBatchResultSchema,
  synthesisVersionSchema,
  tahitiEnvironmentSchema,
  userSchema,
} from '@mss_creator/shared';
import { z } from 'zod';
import { API_BASE_URL } from './api-base';
import { getToken } from './auth-service';
import { notifyUnauthorized } from './session-expiry';

/**
 * Typed fetch wrapper for the backend API.
 *
 * Every response is Zod-validated so a backend schema drift surfaces at the boundary,
 * not deep in a component tree.
 */

class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  /** The `details` of the error envelope, when the server sent some (e.g. a 409 `MEMO_CONFLICT`). */
  public readonly details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Parses the backend's `{ error, message }` error envelope and throws an
 * ApiError — but first, on a 401, tells session-expiry.ts the Tahiti token
 * is dead so it can clear it and redirect to /login. Shared by request<T>()
 * and matrixExportApi.export(), the one call site that can't go through
 * request<T>() because it needs the raw Response for its Blob body.
 */
async function throwOnErrorResponse(res: Response): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as {
    error?: string;
    message?: string;
    details?: unknown;
  };
  if (res.status === 401) notifyUnauthorized();
  throw new ApiError(
    res.status,
    body.error ?? 'UNKNOWN',
    body.message ?? res.statusText,
    body.details,
  );
}

async function request<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    await throwOnErrorResponse(res);
  }

  if (res.status === 204) return undefined as T;

  const json: unknown = await res.json();
  return schema.parse(json);
}

// ── Companies API ─────────────────────────────────────────────────────────────
export const companiesApi = {
  /** The active companies; archived ones are out of the grid. */
  list: (): Promise<Company[]> => request('/companies', z.array(companySchema)),
  /** The archived companies, most recently archived first (pms_user/pms_admin: 403 otherwise). */
  listArchived: (): Promise<ArchivedCompany[]> =>
    request('/companies?archived=true', z.array(archivedCompanySchema)),
  archive: (id: string): Promise<void> =>
    request(`/companies/${id}/archive`, z.void(), { method: 'POST' }),
  restore: (id: string): Promise<void> =>
    request(`/companies/${id}/restore`, z.void(), { method: 'POST' }),
  /** Permanently deletes an archived company (409 while it is still active). */
  delete: (id: string): Promise<void> =>
    request(`/companies/${id}`, z.void(), { method: 'DELETE' }),
  create: (input: CreateCompanyInput): Promise<Company> =>
    request('/companies', companySchema, { method: 'POST', body: JSON.stringify(input) }),
  getById: (id: string): Promise<CompanyWithSetup> =>
    request(`/companies/${id}`, companyWithSetupSchema),
  patch: (id: string, input: PatchCompanyInput): Promise<Company> =>
    request(`/companies/${id}`, companySchema, { method: 'PATCH', body: JSON.stringify(input) }),
  updateSingularitySetup: (id: string, input: UpdateSingularitySetupInput): Promise<void> =>
    request(`/companies/${id}/singularity-setup`, z.object({ updated: z.literal(true) }), {
      method: 'PUT',
      body: JSON.stringify(input),
    }).then(() => undefined),
  updatePaliers: (id: string, input: UpdatePaliersInput): Promise<void> =>
    request(`/companies/${id}/paliers`, z.object({ updated: z.literal(true) }), {
      method: 'PUT',
      body: JSON.stringify(input),
    }).then(() => undefined),

  /**
   * Reads an `.xlsx` into text grids (spec docs/specs/singularity-setup.md,
   * "Import depuis Excel"). Persists nothing: the grids go to the shared
   * import module, which merges them into the page draft. Multipart, so it
   * bypasses `request<T>()` for the reason given on `contextFilesApi`.
   */
  importSingularitySetupFile: async (id: string, file: File): Promise<ImportedWorkbook> => {
    const token = getToken();
    const body = new FormData();
    body.append('file', file);
    const res = await fetch(`${API_BASE_URL}/companies/${id}/singularity-setup/import`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body,
    });
    if (!res.ok) {
      await throwOnErrorResponse(res);
    }
    return importedWorkbookSchema.parse(await res.json());
  },

  /**
   * Stores an imported image as the company's logo, from a neutral framing
   * (issue #20). Multipart, like the other uploads. A refusal carries one of
   * `LOGO_FORMAT_UNSUPPORTED` / `LOGO_TOO_LARGE` as the `ApiError` code.
   */
  uploadLogo: async (id: string, file: File): Promise<Company> => {
    const token = getToken();
    const body = new FormData();
    body.append('file', file);
    const res = await fetch(`${API_BASE_URL}/companies/${id}/logo`, {
      method: 'PUT',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body,
    });
    if (!res.ok) {
      await throwOnErrorResponse(res);
    }
    return companySchema.parse(await res.json());
  },
  /** The server downloads the image once and stores it (`LOGO_URL_UNREACHABLE` when it cannot). */
  importLogoFromUrl: (id: string, url: string): Promise<Company> =>
    request(`/companies/${id}/logo/from-url`, companySchema, {
      method: 'PUT',
      body: JSON.stringify({ url }),
    }),
  /** Saves how the logo sits in its circle; the image is untouched. */
  saveLogoFraming: (id: string, framing: LogoFraming): Promise<Company> =>
    request(`/companies/${id}/logo`, companySchema, {
      method: 'PATCH',
      body: JSON.stringify(framing),
    }),
  /** Removes the stored image, the old URL and the framing. */
  removeLogo: (id: string): Promise<Company> =>
    request(`/companies/${id}/logo`, companySchema, { method: 'DELETE' }),
  /**
   * The creation form's « Depuis une URL »: the server downloads the image like
   * `importLogoFromUrl` but stores nothing — there is no company yet — and sends
   * it back, since the browser cannot fetch another site's image itself.
   */
  previewLogoFromUrl: async (url: string): Promise<Blob> => {
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/companies/logo-preview`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ url }),
    });
    if (!res.ok) {
      await throwOnErrorResponse(res);
    }
    return res.blob();
  },
  /**
   * The bytes of a stored logo (`Company.logo.src`). An `<img>` cannot carry
   * the session's Authorization header, so the image is fetched here and shown
   * from an object URL.
   */
  fetchLogo: async (src: string): Promise<Blob> => {
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}${src}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      await throwOnErrorResponse(res);
    }
    return res.blob();
  },

  /** The pre-filled `.xlsx` template, built from the company's SAVED setup. */
  downloadSingularitySetupTemplate: async (
    id: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/companies/${id}/singularity-setup/template.xlsx`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      await throwOnErrorResponse(res);
    }
    const blob = await res.blob();
    const filename = parseFilenameFromContentDisposition(
      res.headers.get('content-disposition'),
      'setup-singularite.xlsx',
    );
    return { blob, filename };
  },
};

// ── Me API ───────────────────────────────────────────────────────────────────
export const meApi = {
  get: (): Promise<User> => request('/me', userSchema),
  /** Null when the caller has no membership (a 404) — not an error case. */
  getMembership: async (): Promise<MyCompanyMembership | null> => {
    try {
      return await request('/me/company', myCompanyMembershipSchema);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
};

// ── Claude credentials API (pms-only) ───────────────────────────────────────
export const claudeCredentialsApi = {
  list: (): Promise<ClaudeCredential[]> =>
    request('/admin/claude-credentials', z.array(claudeCredentialSchema)),
  create: (input: CreateClaudeCredentialInput): Promise<ClaudeCredential> =>
    request('/admin/claude-credentials', claudeCredentialSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  activate: (id: string): Promise<void> =>
    request(`/admin/claude-credentials/${id}/activate`, z.object({ activated: z.literal(true) }), {
      method: 'PUT',
    }).then(() => undefined),
  delete: (id: string): Promise<void> =>
    request(`/admin/claude-credentials/${id}`, z.void(), { method: 'DELETE' }),
};

// ── Docs data API (pms_admin-only) ──────────────────────────────────────────
export const docsDataApi = {
  get: (): Promise<DocsData> => request('/admin/docs-data', docsDataSchema),
};

// ── Matrices API ─────────────────────────────────────────────────────────────
export const matricesApi = {
  create: (input: CreateMatrixInput): Promise<Matrix> =>
    request('/matrices', matrixSchema, { method: 'POST', body: JSON.stringify(input) }),
  get: (id: string): Promise<MatrixWithDimensions> =>
    request(`/matrices/${id}`, matrixWithDimensionsSchema),
  patch: (id: string, input: PatchMatrixInput): Promise<Matrix> =>
    request(`/matrices/${id}`, matrixSchema, { method: 'PATCH', body: JSON.stringify(input) }),
  /**
   * Zoom (E13). A route of its own, not `patch` above: the generic patch is
   * guarded to `state === 'brouillon'`, and the matrix view page only ever
   * renders a `généré` matrix — routing zoom through it made every click 409.
   */
  patchViewState: (id: string, input: PatchMatrixViewStateInput): Promise<Matrix> =>
    request(`/matrices/${id}/view-state`, matrixSchema, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  /**
   * Sujet & contexte on an already-`généré` matrix (2026-08-26 design doc,
   * D22). A third route rather than a widened `patch` above: that one is
   * `brouillon`-only so a stale wizard tab cannot rewrite the text a running
   * job is composing its prompt from, and the Contexte tab needs exactly the
   * opposite — editing a matrix that has already been generated. Same
   * precedent as `patchViewState` just above. Blocked server-side while a
   * generation is in flight, and stamps `matrices.contextUpdatedAt` so the
   * staleness markers (D25) pick the change up.
   */
  patchContext: (id: string, input: PatchMatrixContextInput): Promise<Matrix> =>
    request(`/matrices/${id}/context`, matrixSchema, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  /** Full copy of a matrix, in the same company (issue #16, `POST /matrices/:id/duplicate`). */
  duplicate: (id: string): Promise<DuplicateMatrixResponse> =>
    request(`/matrices/${id}/duplicate`, duplicateMatrixResponseSchema, { method: 'POST' }),
  /** A new draft pre-filled from a matrix (issue #16, `POST /matrices/:id/derive`). */
  derive: (id: string): Promise<DeriveMatrixResponse> =>
    request(`/matrices/${id}/derive`, deriveMatrixResponseSchema, { method: 'POST' }),
  replaceDimensions: (id: string, input: ReplaceDimensionsInput): Promise<Dimension[]> =>
    request(`/matrices/${id}/dimensions`, z.array(dimensionSchema), {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  /**
   * Matrix-level palier overrides — the most specific of the three levels
   * (issue #1). Same body shape as `companiesApi.updatePaliers`; on a `généré`
   * matrix the write also moves `contextUpdatedAt`, so callers should
   * invalidate the matrix query afterwards.
   */
  updatePaliers: (id: string, input: UpdatePaliersInput): Promise<void> =>
    request(`/matrices/${id}/paliers`, z.object({ updated: z.literal(true) }), {
      method: 'PUT',
      body: JSON.stringify(input),
    }).then(() => undefined),
  getComposedPrompt: (id: string, columnNumbers?: PalierNumber[]): Promise<{ prompt: string }> => {
    const query =
      columnNumbers && columnNumbers.length > 0 ? `?columnNumbers=${columnNumbers.join(',')}` : '';
    return request(`/matrices/${id}/composed-prompt${query}`, composedPromptSchema);
  },
  /**
   * What each past run actually sent — full generations (issue #3) and
   * regeneration batches (issue #6), each list newest first, both empty for a
   * matrix never generated. pms_admin-only server-side, so a 403 here is a
   * normal outcome for every other role, not a bug.
   *
   * Note how little this shares with `getComposedPrompt` above despite the
   * similar name: that one composes a fresh preview of what the *next* run
   * would send, this one reads back what past runs did send. Confusing the two
   * is exactly the failure the Cadrage tab exists to prevent.
   */
  listPromptHistory: (id: string): Promise<MatrixPromptHistory> =>
    request(`/matrices/${id}/generation-prompts`, matrixPromptHistorySchema),
  listForCompany: (companyId: string): Promise<MatrixSummary[]> =>
    request(`/companies/${companyId}/matrices`, z.array(matrixSummarySchema)),
  /**
   * Puts a matrix in a folder, or back « Hors dossier » with `null` (issue #19,
   * `PUT /matrices/:id/folder`; 422 when the folder belongs to another company).
   */
  moveToFolder: (id: string, folderId: string | null): Promise<MoveMatrixToFolderResponse> =>
    request(`/matrices/${id}/folder`, moveMatrixToFolderResponseSchema, {
      method: 'PUT',
      body: JSON.stringify({ folderId }),
    }),
  delete: (id: string): Promise<void> => request(`/matrices/${id}`, z.void(), { method: 'DELETE' }),
  performResize: (id: string, input: PerformResizeInput): Promise<Matrix> =>
    request(`/matrices/${id}/resize`, matrixSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  performVisibility: (id: string, input: PerformVisibilityInput): Promise<Matrix> =>
    request(`/matrices/${id}/visibility`, matrixSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  undo: (id: string): Promise<Matrix> =>
    request(`/matrices/${id}/undo`, matrixSchema, { method: 'POST' }),
  redo: (id: string): Promise<Matrix> =>
    request(`/matrices/${id}/redo`, matrixSchema, { method: 'POST' }),
  /**
   * The raw actions log, newest-first — backs the drawer's Historique tab in
   * its **global** scope (design doc M8). Only ever contains
   * `resize`/`toggle_visibility` entries this pass (E3).
   */
  listActions: (id: string): Promise<ActionLogEntry[]> =>
    request(`/matrices/${id}/actions`, actionLogSchema),
};

// ── Matrix generation API ────────────────────────────────────────────────────
export const matrixGenerationApi = {
  start: (matrixId: string, input: CreateMatrixGenerationJobInput): Promise<MatrixGenerationJob> =>
    request(`/matrices/${matrixId}/generation-jobs`, matrixGenerationJobSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** `null` when no job has ever run for the matrix (a 404) — not an error case. */
  getLatest: async (matrixId: string): Promise<MatrixGenerationJob | null> => {
    try {
      return await request(
        `/matrices/${matrixId}/generation-jobs/latest`,
        matrixGenerationJobSchema,
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
};

// ── Dimension generation API (2026-08-18 matrix-view grill, point 14) ──────
export const dimensionGenerationApi = {
  create: (
    matrixId: string,
    input: CreateDimensionGenerationJobInput,
  ): Promise<CreateDimensionGenerationJobResult> =>
    request(
      `/matrices/${matrixId}/dimensions/generation-jobs`,
      createDimensionGenerationJobResultSchema,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  /** `null` when no add-dimension job has ever run for the matrix (a 404) — not an error case. */
  getLatest: async (matrixId: string): Promise<DimensionGenerationJob | null> => {
    try {
      return await request(
        `/matrices/${matrixId}/dimensions/generation-jobs/latest`,
        dimensionGenerationJobSchema,
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  retry: (jobId: string): Promise<DimensionGenerationJob> =>
    request(`/dimensions/generation-jobs/${jobId}/retry`, dimensionGenerationJobSchema, {
      method: 'POST',
    }),
};

// ── Matrix regeneration API (2026-08-14 design doc) ─────────────────────────
export const matrixRegenerationApi = {
  regenerateMatrix: (matrixId: string, input: RegenerateMatrixInput): Promise<RegenerationBatch> =>
    request(`/matrices/${matrixId}/regenerate-matrix`, regenerationBatchSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  regenerateActivationBlock: (
    matrixId: string,
    input: RegenerateActivationBlockInput,
  ): Promise<RegenerationBatch> =>
    request(`/matrices/${matrixId}/regenerate-activation-block`, regenerationBatchSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  regenerateRow: (matrixId: string, input: RegenerateRowInput): Promise<RegenerationBatch> =>
    request(`/matrices/${matrixId}/regenerate-row`, regenerationBatchSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  regenerateColumn: (matrixId: string, input: RegenerateColumnInput): Promise<RegenerationBatch> =>
    request(`/matrices/${matrixId}/regenerate-column`, regenerationBatchSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  regenerateCell: (matrixId: string, input: RegenerateCellInput): Promise<RegenerationBatch> =>
    request(`/matrices/${matrixId}/regenerate-cell`, regenerationBatchSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  regenerateCells: (matrixId: string, input: RegenerateCellsInput): Promise<RegenerationBatch> =>
    request(`/matrices/${matrixId}/regenerate-cells`, regenerationBatchSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** `null` when no batch has ever run for the matrix (a 404) — not an error case. */
  getLatestBatch: async (matrixId: string): Promise<RegenerationBatch | null> => {
    try {
      return await request(
        `/matrices/${matrixId}/regeneration-batches/latest`,
        regenerationBatchSchema,
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  /**
   * The prompt a regeneration of this scope would send (issue #6), split into
   * the editable body and the locked JSON-output tail.
   *
   * POST, unlike `matricesApi.getComposedPrompt`: a multiCell selector carries
   * an array of UUIDs and the comment runs to 4000 characters, neither of
   * which fits in a query string. pms_admin-only server-side.
   */
  getPrompt: (
    matrixId: string,
    input: RegenerationPromptPreviewInput,
  ): Promise<RegenerationPromptPreview> =>
    request(`/matrices/${matrixId}/composed-prompt`, regenerationPromptPreviewSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** Non-destructive batch restore (I2) — skips cells whose current version is a manual edit newer than the batch. */
  restoreBatch: (batchId: string): Promise<RestoreRegenerationBatchResult> =>
    request(`/regeneration-batches/${batchId}/restore`, restoreRegenerationBatchResultSchema, {
      method: 'POST',
    }),
};

// ── Folders API ──────────────────────────────────────────────────────────────

/** The matrix folders of a company (issue #19). A name already taken answers 409 `FOLDER_NAME_TAKEN`. */
export const foldersApi = {
  /** Alphabetical (case and accents ignored), with the matrix count and the visible open notes. */
  listForCompany: (companyId: string): Promise<MatrixFolder[]> =>
    request(`/companies/${companyId}/folders`, z.array(matrixFolderSchema)),
  create: (companyId: string, input: CreateFolderInput): Promise<MatrixFolder> =>
    request(`/companies/${companyId}/folders`, matrixFolderSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** Rename and/or describe; an empty description clears it. */
  update: (id: string, input: UpdateFolderInput): Promise<MatrixFolder> =>
    request(`/matrix-folders/${id}`, matrixFolderSchema, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  /** Its matrices go back « Hors dossier »; its description and notes are deleted. */
  remove: (id: string): Promise<void> =>
    request(`/matrix-folders/${id}`, z.void(), { method: 'DELETE' }),
};

// ── Notes API ────────────────────────────────────────────────────────────────

/**
 * Matrix and folder notes (issue #19). Every list is already filtered by what
 * the caller may read; the client never has to hide a note.
 */
export const notesApi = {
  /** The memo and the visible discussions of a matrix. */
  listForMatrix: (matrixId: string): Promise<MatrixNotesResponse> =>
    request(`/matrices/${matrixId}/notes`, matrixNotesResponseSchema),
  /** The visible discussions of a folder. */
  listForFolder: (folderId: string): Promise<FolderNotesResponse> =>
    request(`/matrix-folders/${folderId}/notes`, folderNotesResponseSchema),
  /** A new discussion on the matrix, a cell, a row or a column (403 for a client role writing at the PMS level). */
  createForMatrix: (matrixId: string, input: CreateMatrixNoteInput): Promise<NoteThread> =>
    request(`/matrices/${matrixId}/notes`, noteThreadSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  createForFolder: (folderId: string, input: CreateFolderNoteInput): Promise<NoteThread> =>
    request(`/matrix-folders/${folderId}/notes`, noteThreadSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** Answers a root note (422 on a reply); the reply takes the level of its root. */
  reply: (noteId: string, input: ReplyNoteInput): Promise<Note> =>
    request(`/notes/${noteId}/replies`, noteSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** Author only (403 otherwise); `visibility` is for a root note. */
  update: (noteId: string, input: UpdateNoteInput): Promise<Note> =>
    request(`/notes/${noteId}`, noteSchema, { method: 'PATCH', body: JSON.stringify(input) }),
  /** Author only; deleting a root note deletes its replies. */
  remove: (noteId: string): Promise<void> =>
    request(`/notes/${noteId}`, z.void(), { method: 'DELETE' }),
  /** Closes a whole discussion; anyone who can see it may. */
  resolve: (noteId: string): Promise<NoteThread> =>
    request(`/notes/${noteId}/resolve`, noteThreadSchema, { method: 'POST' }),
  reopen: (noteId: string): Promise<NoteThread> =>
    request(`/notes/${noteId}/reopen`, noteThreadSchema, { method: 'POST' }),
  /**
   * Writes the memo. `baseUpdatedAt` is the `memo.updatedAt` last read: when the
   * memo has changed since, the answer is 409 `MEMO_CONFLICT`.
   */
  updateMemo: (matrixId: string, input: UpdateMemoInput): Promise<MatrixMemo> =>
    request(`/matrices/${matrixId}/memo`, matrixMemoSchema, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
};

// ── Cells API ────────────────────────────────────────────────────────────────
export const cellsApi = {
  listForMatrix: (matrixId: string): Promise<Cell[]> =>
    request(`/matrices/${matrixId}/cells`, z.array(cellSchema)),
  createVersion: (cellId: string, input: CreateCellVersionInput): Promise<Cell> =>
    request(`/cells/${cellId}/versions`, cellSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /**
   * Non-destructive restore (design doc M9): the server inserts a *new*
   * version copying the target's content/origin and repoints
   * `currentVersionId`. No body — the URL fully identifies the target.
   */
  restoreVersion: (cellId: string, versionId: string): Promise<Cell> =>
    request(`/cells/${cellId}/versions/${versionId}/restore`, cellSchema, { method: 'POST' }),
};

// ── Context files API ────────────────────────────────────────────────────────

/**
 * Uploaded documents that feed generation as extra context (2026-08-26 design
 * doc, D5-D8).
 *
 * `upload` cannot go through `request<T>()`: that helper forces
 * `content-type: application/json`, whereas a multipart body needs the browser
 * to set the header itself so it can add the boundary. Setting it by hand
 * produces a body the server cannot parse.
 */
export const contextFilesApi = {
  list: (matrixId: string): Promise<ContextFile[]> =>
    request(`/matrices/${matrixId}/context-files`, z.array(contextFileSchema)),

  upload: async (matrixId: string, file: File): Promise<ContextFile> => {
    const token = getToken();
    const body = new FormData();
    body.append('file', file);

    const res = await fetch(`${API_BASE_URL}/matrices/${matrixId}/context-files`, {
      method: 'POST',
      // No content-type here, deliberately — see the note above.
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body,
    });

    if (!res.ok) {
      await throwOnErrorResponse(res);
    }
    return contextFileSchema.parse(await res.json());
  },

  remove: (matrixId: string, fileId: string): Promise<void> =>
    request(`/matrices/${matrixId}/context-files/${fileId}`, z.void(), { method: 'DELETE' }),
};

// ── Digests API ──────────────────────────────────────────────────────────────

/**
 * The two-level digest over a matrix's context files (2026-08-26 design doc,
 * D9-D19): one digest per file, one cross-document synthesis per matrix.
 *
 * Note the deliberate asymmetry in how long these calls take. `getState`,
 * `editFileDigest` and `editSynthesis` are plain database round trips. The two
 * `regenerate*` calls make a real model call and return **synchronously** —
 * minutes, not milliseconds — because the reviewer is sitting in front of the
 * result and a job plus a poll would add latency for no gain (see the note at
 * the top of `backend/src/routes/digests.ts`). `startDigestJob` is the
 * opposite case: digesting a whole matrix is several model calls, so it goes
 * on the polled `matrix_generation_jobs` machinery (D15) and is followed with
 * `matrixGenerationApi.getLatest`.
 */
export const digestsApi = {
  getState: (matrixId: string): Promise<MatrixDigestState> =>
    request(`/matrices/${matrixId}/digest-state`, matrixDigestStateSchema),

  /** Optionally steered by a comment, recorded on the version it produced and never reapplied (D18). */
  regenerateFileDigest: (
    matrixId: string,
    fileId: string,
    input: RegenerateDigestInput = {},
  ): Promise<DigestVersion> =>
    request(`/matrices/${matrixId}/context-files/${fileId}/digest-versions`, digestVersionSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Hand-written replacement, stored as a new `manual` version (D19). No model call. */
  editFileDigest: (
    matrixId: string,
    fileId: string,
    input: EditDigestInput,
  ): Promise<DigestVersion> =>
    request(`/matrices/${matrixId}/context-files/${fileId}/digest`, digestVersionSchema, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  /** Needs at least two digested files — with one, that digest *is* the context (D11). */
  regenerateSynthesis: (
    matrixId: string,
    input: RegenerateDigestInput = {},
  ): Promise<SynthesisVersion> =>
    request(`/matrices/${matrixId}/synthesis-versions`, synthesisVersionSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  editSynthesis: (matrixId: string, input: EditDigestInput): Promise<SynthesisVersion> =>
    request(`/matrices/${matrixId}/synthesis`, synthesisVersionSchema, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  /**
   * Digests every file that lacks one, then the synthesis, and stops there —
   * a `digest_only` job on the same table as a generation, which is what keeps
   * a manual digest and a generation from telescoping (D15). An optional comment steers every
   * digest of the run and the synthesis that follows (issue #20).
   */
  startDigestJob: (
    matrixId: string,
    input: StartDigestJobInput = {},
  ): Promise<MatrixGenerationJob> =>
    request(`/matrices/${matrixId}/digest-jobs`, matrixGenerationJobSchema, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

// ── Feature flags API ────────────────────────────────────────────────────────
export const featureFlagsApi = {
  list: (): Promise<FeatureFlag[]> => request('/feature-flags', z.array(featureFlagSchema)),
  set: (key: string, value: string): Promise<FeatureFlag> =>
    request(`/feature-flags/${key}`, featureFlagSchema, {
      method: 'PATCH',
      body: JSON.stringify({ value }),
    }),
};

// ── Tahiti environment API (public: used by the login page, no session) ─────
export const tahitiEnvironmentApi = {
  get: (): Promise<TahitiEnvironment> => request('/tahiti-environment', tahitiEnvironmentSchema),
  set: (value: TahitiEnvironmentValue): Promise<TahitiEnvironment> =>
    request('/tahiti-environment', tahitiEnvironmentSchema, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    }),
};

// ── Matrix export API ───────────────────────────────────────────────────────
export function parseFilenameFromContentDisposition(
  header: string | null,
  fallback = 'export',
): string {
  if (!header) return fallback;
  const match = /filename="([^"]+)"/.exec(header);
  return match?.[1] ?? fallback;
}

/**
 * Reads the PDF branch's render facts header (issue #11). An external input
 * like any response body, so it is validated — and a missing or malformed
 * header reads as "no facts" rather than failing the export: the file itself
 * is fine, only the overlays' geometry would be unsure of the logo and dates.
 */
export function parseRenderFactsHeader(header: string | null): ExportRenderFacts | null {
  if (header === null) return null;
  let json: unknown;
  try {
    json = JSON.parse(header);
  } catch {
    return null;
  }
  const parsed = exportRenderFactsSchema.safeParse(json);
  return parsed.success ? parsed.data : null;
}

export interface ExportResult {
  blob: Blob;
  filename: string;
  /** Only the PDF branch sends them; `null` for excel/html or an unreadable header. */
  facts: ExportRenderFacts | null;
}

export const matrixExportApi = {
  export: async (
    matrixId: string,
    input: ExportRequest,
    init?: { signal?: AbortSignal },
  ): Promise<ExportResult> => {
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/matrices/${matrixId}/export`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(input),
      // Aborting tells the backend the render is no longer wanted: a preview
      // superseded by a newer setting is skipped if it is still queued.
      ...(init?.signal !== undefined ? { signal: init.signal } : {}),
    });
    if (!res.ok) {
      await throwOnErrorResponse(res);
    }
    const blob = await res.blob();
    const filename = parseFilenameFromContentDisposition(res.headers.get('content-disposition'));
    const facts = parseRenderFactsHeader(res.headers.get(EXPORT_RENDER_FACTS_HEADER));
    return { blob, filename, facts };
  },
};

/** Triggers a real browser download from an in-memory Blob — no server round trip involved beyond the fetch that produced it. */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export { request, ApiError };
